/**
 * Firebase Auth（ソーシャルログイン）の接続先と、出すログイン方法。
 * 未設定でもアプリは動く。診断は匿名で完結し、ログインに関わるものだけ「準備中」になる。
 * firebase 本体は import しない——ここは初期バンドルに乗るため。
 */
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY?.trim() ?? '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim() ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim() ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID?.trim() ?? '',
};

export const authEnabled = Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId);

/**
 * 使えるソーシャルアカウント。**どれを出すかは `VITE_AUTH_PROVIDERS`**（カンマ区切り、既定 `google,password`）。
 * ここに並べたものは Firebase コンソールの Authentication → Sign-in method で
 * 有効にしておく必要がある（していないと `auth/operation-not-allowed` になる。doc/DEPLOY.md §2）。
 *
 * LINE だけは Firebase のコンソールでは有効にせず、サーバに `LINE_CHANNEL_ID` `LINE_CHANNEL_SECRET` を入れる
 * （カスタムトークンで入る。doc/AUTH.md §21）。
 *
 * 管理者になれるのは Google で入った人だけ（doc/AUTH.md §3）。ほかのアカウントのメールアドレスは
 * そのサービスで自由に変えられるため、管理者の判定には使わない。
 */
export const AUTH_PROVIDERS = ['google', 'line', 'password', 'twitter', 'github', 'facebook'] as const;
export type AuthProviderId = (typeof AUTH_PROVIDERS)[number];

export const PROVIDER_LABELS: Record<AuthProviderId, string> = {
  google: 'Google',
  line: 'LINE',
  password: 'メールアドレス',
  twitter: 'X（Twitter）',
  github: 'GitHub',
  facebook: 'Facebook',
};

/** ID トークンの `sign_in_provider` から、画面に出す名前へ。 */
export const providerLabelOf = (signInProvider: string | null): string =>
  ({ 'google.com': 'Google', line: 'LINE', 'twitter.com': 'X（Twitter）', 'github.com': 'GitHub', 'facebook.com': 'Facebook', password: 'メールアドレス' })[
    signInProvider ?? ''
  ] ?? (signInProvider ?? '不明');

function parseProviders(raw: string | undefined): AuthProviderId[] {
  const wanted = (raw ?? '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter((value): value is AuthProviderId => (AUTH_PROVIDERS as readonly string[]).includes(value));
  return wanted.length > 0 ? [...new Set(wanted)] : ['google', 'password'];
}

export const enabledProviders: readonly AuthProviderId[] = parseProviders(import.meta.env.VITE_AUTH_PROVIDERS);

/** ポップアップで入る外部サービス（メール・パスワードはフォームなので別扱い）。 */
export const socialProviders = enabledProviders.filter((id) => id !== 'password');
export const passwordEnabled = enabledProviders.includes('password');
